import { format } from "date-fns";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { ISlotsState } from "@/app_redux/reducers/slices/auth/slots_slice";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import SimplePageHeading from "@/app/components/ui/PageHeadings/SimplePageHeading";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useOrgClinicProvidersAPI } from "@/app/hooks/api_hooks/org/useOrgClinicProvidersAPI";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import { useOverBookingPirmitAPI } from "@/app/hooks/api_hooks/bookings/useOverBookingPostAPI";
import { IPatientWaitingListState } from "@/app_redux/reducers/slices/auth/patient_waiting_list_slice";
import { useOverBookingBdeUserListAPI } from "@/app/hooks/api_hooks/bookings/useOverBookingBdeUserListAPI";

export interface IBookingCommentsFormFields {
  city: string;
  cityId: string;
  clinic: string;
  clinicId: string;
  date: string;
  provider: string;
  providerId: string;
  userName: string;
  userId: string;
  startTime: string;
  startTimemin: string;
  endTime: string;
  endTimemin: string;
}

const OverBooking = () => {
  const currentDate = format(new Date(), "yyyy-MM-dd");
  const arrayConstants = useArrayConstants();
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("SLOTS", "id");
  const fetchCities = useOrgCitiesListAPI("SLOTS");
  const fetchClinicProvider = useOrgClinicProvidersAPI("SLOTS");
  const fetchOverBooking = useOverBookingPirmitAPI();
  const fetchUserList = useOverBookingBdeUserListAPI();
  const { routeState } = useRouteInfo(getRouteKey("SLOTS", "id"));
  const { routeState: userOrg } = useRouteInfo(routeId);
  const {
    cities: cityList,
    clinics: clinicsOrg,
    providers: providerList,
  } = routeState.state as IPatientWaitingListState;
  const startTimeItem = arrayConstants.overBookingStartTimeItems as ISelectMenuItemData[];
  const startTimeMinuteItem = arrayConstants.overBookingStartTimeMinuteItems as ISelectMenuItemData[];
  const { bdeUserList } = userOrg.state as ISlotsState;
  const stringConstants = useStringConstants();
  const cityOrgItem = useSelectMenuReducer(cityList, "name", "id");
  const clinicsOrgItem = useSelectMenuReducer(clinicsOrg, "display_name", "id");
  const bdeUserListItem = useSelectMenuReducer(bdeUserList, "name", "user_id");
  const providerListItem = useSelectMenuReducer(providerList, "name", "staff_id");
  const navigate = useNavigate();
  const { handleSubmit } = useForm();

  const formHook = useForm<IBookingCommentsFormFields>({
    mode: "onChange",
    defaultValues: {
      date: currentDate,
    },
  });

  useEffect(() => {
    fetchCities({
      withClinics: true,
      withClinicProviders: false,
      ROTADate: formHook.getValues("date"),
      staffCategoryId: "2",
    });
    fetchUserList();
  }, []);

  const onSubmit = (_data) => {
    formHook.trigger();
    const form_data = formHook.getValues();
    fetchOverBooking({
      city: {
        id: form_data?.cityId,
        name: form_data?.city,
      },
      clinic: {
        id: form_data?.clinicId,
        name: form_data?.clinic,
      },
      staff: {
        id: form_data?.providerId,
        name: form_data?.provider,
      },
      allowed_date: form_data?.date,
      from_time: form_data?.startTime + ":" + form_data?.startTimemin,
      to_time: form_data?.endTime + ":" + form_data?.endTimemin,
      vms_user: {
        id: form_data?.userId,
        name: form_data?.userName,
      },
    }).then(() => {
      navigate(getRouteKey("OVER_BOOKING_PERMISION_LIST", "url"));
    });
  };

  const handelPermisionButton = () => {
    const form_data = formHook.getValues();
    if (
      form_data.city &&
      form_data.clinic &&
      form_data.date &&
      form_data.startTime &&
      form_data.endTime &&
      form_data.provider &&
      form_data.startTimemin &&
      form_data.endTimemin &&
      form_data.userId
    ) {
      return false;
    } else {
      return true;
    }
  };
  return (
    <form className="bg-white px-4 py-4 pb-8" onSubmit={handleSubmit(onSubmit)}>
      <div className="mx-auto w-full">
        <SimplePageHeading
          description="Allow overbooking for a staff so that he can be allowed to receive overbookings"
          title="Allow Overbooking"
        />
        <div className="w-2/4 mt-4 mx-auto border border-gray-50 shadow-lg rounded-lg p-4">
          <table className="w-full">
            <tr>
              <td className="w-1/4 py-3 text-xs font-medium">Select City</td>
              <td className="py-3">
                <input type="hidden" {...formHook.register("cityId")} />
                <SearchableSelectMenu
                  errorMessages={[{ message: "City is required", type: "required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook.setValue("city", item.title);
                      formHook.setValue("cityId", item.id);
                      fetchCities({
                        withClinics: true,
                        withClinicProviders: false,
                        ROTADate: formHook?.getValues("date"),
                        staffCategoryId: "2",
                        clinicOptions: {
                          cityId: item.id,
                        },
                      });
                    }
                  }}
                  fieldError={formHook.formState.errors.city}
                  register={formHook.register("city", { required: true })}
                  selectItems={cityOrgItem}
                  placeholder="Select City"
                  showTooltips={false}
                  showTypedErrors={true}
                  showDropdownIcon={true}
                  defaultSelected={cityOrgItem?.filter((oc) => oc.title === formHook.watch("city"))[0]}
                  listBoxClassName="w-full"
                  className="text-gray-800"
                  containerClassName="w-full"
                />
              </td>
            </tr>
            <tr>
              <td className="w-1/4 py-3 text-xs font-medium">Select Clinic</td>
              <td className="py-3">
                <input type="hidden" {...formHook.register("clinicId")} />
                <SearchableSelectMenu
                  errorMessages={[{ message: "Clinic is required", type: "required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook.setValue("clinic", item.title);
                      formHook.setValue("clinicId", item.id);
                      fetchClinicProvider({
                        ROTADate: formHook.getValues("date"),
                        clinicIds: [item.id],
                      });
                    }
                  }}
                  showTypedErrors={true}
                  fieldError={formHook.formState.errors.clinic}
                  register={formHook.register("clinic", { required: true })}
                  selectItems={clinicsOrgItem}
                  placeholder="Select Clinics"
                  showTooltips={false}
                  showDropdownIcon={true}
                  defaultSelected={clinicsOrgItem?.filter((oc) => oc.title === formHook.watch("clinic"))[0]}
                  listBoxClassName="w-full"
                  className="text-gray-800"
                  containerClassName="w-full"
                />{" "}
              </td>
            </tr>
            <tr>
              <td className="w-1/4 py-3 text-xs font-medium">Select Date</td>
              <td className="py-3">
                <PWLCalendarComponent
                  errorMessages={[{ message: "Date is required", type: "required" }]}
                  register={formHook.register("date", {
                    required: true,
                  })}
                  showFutureYears
                  disableDatesBeforeDate={format(new Date(Date.now()), stringConstants?.fieldDOBFormat)}
                  fieldError={formHook.formState.errors.date}
                  onSelectDate={(date) => {
                    formHook.setValue("date", format(date, "yyyy-MM-dd"));
                    fetchClinicProvider({
                      ROTADate: format(date, "yyyy-MM-dd"),
                      clinicIds: [formHook?.getValues("clinicId")],
                    });
                  }}
                  className="w-full"
                />
              </td>
            </tr>
            <tr>
              <td className="w-1/4 py-3 text-xs font-medium">Select Provider</td>
              <td className="py-3">
                <input type="hidden" {...formHook.register("providerId")} />
                <SearchableSelectMenu
                  errorMessages={[{ message: "Clinic is provider", type: "required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook.setValue("provider", item.title);
                      formHook.setValue("providerId", item.id);
                    }
                  }}
                  fieldError={formHook.formState.errors.provider}
                  register={formHook.register("provider", { required: true })}
                  selectItems={providerListItem}
                  placeholder="Select Provider"
                  showTooltips={false}
                  showTypedErrors={false}
                  showDropdownIcon={true}
                  defaultSelected={providerListItem?.filter((oc) => oc.title === formHook.watch("provider"))[0]}
                  listBoxClassName="w-full"
                  className="text-gray-800 "
                  containerClassName="w-full"
                  showDropdownWhenNoData
                  dropdownNoDataContent={
                    <div className="text-sm">
                      <p className="text-gray-800">No Providers</p>
                      <p className="text-gray-500">
                        Please either select correct filter or update ROTA for the staffs under clinics
                      </p>
                    </div>
                  }
                />{" "}
              </td>
            </tr>
            <tr>
              <td className="w-1/4 py-3 text-xs font-medium">Select Duration</td>
              <td className="py-3 flex w-full gap-3">
                <div className="2/4 flex flex-col gap-1">
                  <h2 className="font-medium text-xs">Start Time</h2>
                  <div className="flex gap-1">
                    <NonSearchableSelectMenu
                      errorMessages={[{ message: "startTime is required", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue("startTime", item.title);
                        }
                      }}
                      fieldError={formHook.formState.errors.startTime}
                      register={formHook.register("startTime", { required: true })}
                      selectItems={startTimeItem}
                      placeholder="HH"
                      showTooltips={false}
                      showTypedErrors={false}
                      showDropdownIcon={true}
                      defaultSelected={startTimeItem?.filter((oc) => oc.title === formHook.watch("startTime"))[0]}
                      listBoxClassName="w-full"
                      className="text-gray-800"
                      containerClassName="w-24"
                    />{" "}
                    <NonSearchableSelectMenu
                      errorMessages={[{ message: "startTimeMinuteItem is required", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue("startTimemin", item.title);
                        }
                      }}
                      fieldError={formHook.formState.errors.startTimemin}
                      register={formHook.register("startTimemin", { required: true })}
                      selectItems={startTimeMinuteItem}
                      placeholder="MM"
                      showTooltips={false}
                      showTypedErrors={false}
                      showDropdownIcon={true}
                      defaultSelected={
                        startTimeMinuteItem?.filter((oc) => oc.title === formHook.watch("startTimemin"))[0]
                      }
                      listBoxClassName="w-full"
                      className="text-gray-800"
                      containerClassName="w-24"
                    />{" "}
                  </div>
                </div>
                <div className="2/4 flex flex-col gap-1">
                  <h2 className="font-medium text-xs">End Time</h2>
                  <div className="flex gap-1">
                    <NonSearchableSelectMenu
                      errorMessages={[{ message: "endTime is required", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue("endTime", item.title);
                        }
                      }}
                      fieldError={formHook.formState.errors.endTime}
                      register={formHook.register("endTime", { required: true })}
                      selectItems={startTimeItem}
                      placeholder="HH"
                      showTooltips={false}
                      showTypedErrors={false}
                      showDropdownIcon={true}
                      defaultSelected={startTimeItem?.filter((oc) => oc.title === formHook.watch("endTime"))[0]}
                      listBoxClassName="w-full"
                      className="text-gray-800"
                      containerClassName="w-24"
                    />{" "}
                    <NonSearchableSelectMenu
                      errorMessages={[{ message: "endTimemin is required", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue("endTimemin", item.title);
                        }
                      }}
                      fieldError={formHook.formState.errors.endTimemin}
                      register={formHook.register("endTimemin", { required: true })}
                      selectItems={startTimeMinuteItem}
                      placeholder="MM"
                      showTooltips={false}
                      showTypedErrors={false}
                      showDropdownIcon={true}
                      defaultSelected={
                        startTimeMinuteItem?.filter((oc) => oc.title === formHook.watch("endTimemin"))[0]
                      }
                      listBoxClassName="w-full"
                      className="text-gray-800"
                      containerClassName="w-24"
                    />{" "}
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td className="w-1/4 py-3 text-xs font-medium">Select User</td>
              <td className="py-3">
                <input type="hidden" {...formHook.register("userId")} />
                <SearchableSelectMenu
                  errorMessages={[{ message: "User is required", type: "required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook.setValue("userName", item.title);
                      formHook.setValue("userId", item.id);
                    }
                  }}
                  fieldError={formHook.formState.errors.userName}
                  register={formHook.register("userName", { required: true })}
                  selectItems={bdeUserListItem}
                  placeholder="Select User"
                  showTooltips={false}
                  showTypedErrors={false}
                  showDropdownIcon={true}
                  defaultSelected={bdeUserListItem?.filter((oc) => oc.title === formHook.watch("userName"))[0]}
                  listBoxClassName="w-full"
                  className="text-gray-800"
                  containerClassName="w-full"
                />{" "}
              </td>
            </tr>
          </table>
          <div className="mt-6 w-full">
            <input
              type="submit"
              value="Permit Overbooking"
              disabled={handelPermisionButton()}
              className={`py-3 cursor-pointer text-sm w-full ${
                !handelPermisionButton() ? "bg-[#193A6A]" : "bg-gray-400"
              } text-white font-semibold rounded-md`}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default OverBooking;
